import {Inject, injectable} from 'inversify-props';

import {ILogger, LogLevel} from '@/utils/ILogger';
import {LogdownLogger} from '@/utils/LogdownLogger';

import {Status, MessageReceivedCallback, OnDisconnectedHandler} from '@/model/Messaging';

import {
  BrowserWebSocketTransport,
  Connection,
  ConnectionCloseInfo,
  EMatchPolicy,
  IPublication,
  ISubscription,
  JSONSerializer,
  LogLevel as kLogLevel,
  WelcomeDetails,
} from '@verkehrsministerium/kraftfahrstrasse';

import Jwt from '../model/Jwt';
import {JwtAuthProvider} from '@/utils/JwtAuthProvider';
import {IApiService} from '@/services/IApi.service';

export enum SubscriptionStatus {
  Idle,
  Pending,
  Subscribing,
  Subscribed,
  Unsubscribed,
}

export class Subscription {

  public subscription!: ISubscription;

  public status: SubscriptionStatus = SubscriptionStatus.Idle;

  constructor(public topic: string, public matchPolicy: EMatchPolicy, public callback: MessageReceivedCallback) {

  }
}

@injectable()
export class MessagingStore {

  private static REALM = 'manager';
  private static PATH = 'api/v1/events';

  public DisconnectedHandlers: OnDisconnectedHandler[] = [];

  public Status: Status = Status.Disconnected;

  private logger: ILogger = new LogdownLogger('MessagingStore');
  private transportLogger: ILogger = new LogdownLogger('MessagingServiceLibrary');

  @Inject(Symbol.for('IApiService'))
  private apiService!: IApiService;

  private connection!: Connection;

  private subscriptions: Subscription[] = [];

  public async connect(remote: string, secured: boolean, token: Jwt): Promise<WelcomeDetails> {

    switch (this.Status) {
      case Status.Disconnected:
      case Status.Error:
        break;

      default:
        throw new Error('cannot connect messaging, current status is ' + this.Status);
    }

    /* Check token  */
    if (token === null) {
      const error = new Error(`No authentication token provided, cannot connect`);
      this.logger.error(error.message);
      throw error;
    }

    const endpoint = `${secured ? 'wss' : 'ws'}://${remote}/${MessagingStore.PATH}`;
    const realm = MessagingStore.REALM;

    /* Create the new connection object with options */
    this.connection = new Connection({
      endpoint,
      realm,

      serializer: new JSONSerializer(),
      transport: BrowserWebSocketTransport,
      authProvider: new JwtAuthProvider(token.string), // Pass an **instance** of a IAuthProvider to authenticate
      logFunction: this.LogConverter.bind(this), // Optionally, pass a function used to debug kraftfahrstrasse
    });

    /* Connect to the server */
    this.logger.info('Connecting...', endpoint, realm);
    try {
      this.connection.OnClose()
        .then((closeInfo) => this.onDisconnected(closeInfo))
        .catch((closeInfo) => this.onDisconnected(closeInfo));

      this.Status = Status.Connecting;

      const welcomeDetails = await this.connection.Open(); // wait until the connection is opened, after this point, you may use
      this.logger.info('Connected', welcomeDetails);

      this.Status = Status.Connected;

      this.onConnected();

      return welcomeDetails;
    } catch (error) {
      this.Status = Status.Disconnected;
      this.logger.error('Could not connect to server', error);
      throw error;
    }
  }

  public async disconnect(): Promise<ConnectionCloseInfo> {

    switch (this.Status) {
      case Status.Connected:
      case Status.Error:
        break;

      default:
        throw new Error('not connected');
    }

    let closeInfo: ConnectionCloseInfo;
    try {
      this.Status = Status.Disconnecting;
      closeInfo = await this.connection.Close();
      this.logger.info(`Connection has been close by client`, closeInfo);
    } catch (error) {
      this.Status = Status.Error;
      throw error;
    }

    return closeInfo;
  }

  public async subscribe(topic: string, callback: MessageReceivedCallback, matchPolicy: EMatchPolicy = EMatchPolicy.Exact): Promise<Subscription> {

    this.logger.debug(`Subscribing to '${topic}' with policy '${matchPolicy}'...`);

    const subscription = new Subscription(topic, matchPolicy, callback);

    if (this.Status === Status.Connected) {
      await this.subscribeSubscription(subscription);
    } else {
      subscription.status = SubscriptionStatus.Pending;
      const msg = `Subscription to '${topic}' delayed: messaging service not connected`;
      this.logger.debug(msg);
    }

    this.subscriptions.push(subscription);
    return subscription;
  }

  public async subscribeSubscription(subscription: Subscription): Promise<void> {

    if (subscription.status === SubscriptionStatus.Subscribing || subscription.status === SubscriptionStatus.Subscribed) {
      const msg = `Already '${[subscription.status]}' to '${subscription.topic}'`;
      this.logger.debug(msg);
      throw new Error(msg);
    }

    try {

      subscription.status = SubscriptionStatus.Subscribing;
      subscription.subscription = await this.connection.Subscribe(
        subscription.topic,
        subscription.callback,
        {
          match: subscription.matchPolicy,
        },
      );

      subscription.status = SubscriptionStatus.Subscribed;

      this.logger.debug(`Subscribed to ${subscription.topic}`, this);

    } catch (error) {
      const msg = `Could not subscribe to '${subscription.topic}'`;
      this.logger.error(msg, error);
      throw new Error(`${msg}: ${error}`);
    }
  }

  public async unsubscribe(subscription: Subscription): Promise<void> {
    await subscription.subscription.Unsubscribe();

    subscription.status = SubscriptionStatus.Unsubscribed;

    this.subscriptions.splice(this.subscriptions.indexOf(subscription), 1);
  }

  public async send(topic: string, message: string): Promise<IPublication> {

    this.logger.debug(`Sending message '${message}' to topic ${topic}...`);

    if (this.Status !== Status.Connected) {
      const msg = `Cannot send message to '${topic}': messaging service not connected`;
      this.logger.error(msg);
      throw new Error(`${msg}`);
    }

    try {
      const publication = await this.connection.Publish(topic, [message]);
      this.logger.debug('Message sent', publication);
      return publication;
    } catch (error) {
      this.logger.error(`Could not publish '${message}' to '${topic}'`, error);
      throw error;
    }
  }

  private async onConnected(): Promise<void> {
    if (this.subscriptions.length === 0) {
      this.logger.debug(`No subscriptions`);
      return;
    }

    this.logger.info(`Reconnecting subscriptions...`);
    for (const subscription of this.subscriptions) {
      if (subscription.status === SubscriptionStatus.Pending || subscription.status === SubscriptionStatus.Idle) {
        await this.subscribeSubscription(subscription);
      }
    }
  }

  private async onDisconnected(closeInfo: ConnectionCloseInfo): Promise<void> {
    this.logger.info(`Connection closed:`, closeInfo);
    this.Status = Status.Disconnected;

    for (const subscription of this.subscriptions) {
      subscription.status = SubscriptionStatus.Idle;
    }

    for (const handler of this.DisconnectedHandlers) {
      handler(closeInfo).catch((error: Error) => this.logger.error(`Disconnected handler error`, error));
    }
  }

  private LogConverter(logLevel: kLogLevel, timestamp: Date, fileName: string, logText: string) {

    let level = LogLevel.TRACE;
    switch (logLevel) {
      case kLogLevel.DEBUG:
        level = LogLevel.DEBUG;
        break;
      case kLogLevel.INFO:
        level = LogLevel.DEBUG;
        break;
      case kLogLevel.WARNING:
        level = LogLevel.WARN;
        break;
      case kLogLevel.ERROR:
        level = LogLevel.ERROR;
        break;
    }

    this.transportLogger.log(level, `${timestamp.toISOString()} - ${logText} (${fileName})`);
  }
}
